import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { roleNameKey, useLocale, useT } from '../../i18n';
import {
  PLATFORM_ROLES,
  fetchPlatformUsers,
  grantPlatformRole,
  revokePlatformRole,
  setAccountStatus,
  closeAccount as closeAccountCall,
  CLOSURE_REASONS,
  SUPPORT_REFERENCE,
  type ClosureReason,
  type DirectoryResult,
  type PlatformRole,
  type PlatformUser,
} from '../../lib/api/platformUsers';
import { safeId } from '../ui/labelledControl';

// ─────────────────────────────────────────────────────────────────────
// /sa → Platform users: who may do what in this panel, and how somebody new
// gets there (#975, the owner's decision of 2026-10-19).
//
// TWO SYSTEMS, ONE JOB EACH.
//
//   • Microsoft Entra ID AUTHENTICATES StarPoint staff. A person is assigned
//     to the StarPoint staff app in Entra and signs in with Microsoft.
//     StarPoint cannot do that step and this page does not pretend to.
//   • /sa AUTHORIZES. A platform role is given to an account that has ALREADY
//     signed in — picked from the directory the server built from real
//     sign-ins, never typed in as an address.
//
// So the page reads as the onboarding path, in order: the step guide (Entra
// assignment → first Microsoft sign-in → a platform role here), then who holds
// which platform role, then — kept apart on purpose — the accounts that hold a
// platform role WITHOUT signing in with Microsoft, so leftovers (an old demo
// account, the dev staff door) are visible and can be cleaned up.
//
// NO NEW API AND NO NEW LOGIC. Every list is a client-side split of the same
// `GET /platform/users` rows, by identity PROVIDER and by whether a platform
// role is held; every write is the grant/revoke/status call this section
// already made. There is still no free-text «grant to this e-mail» field: the
// step-3 picker offers only accounts the server returned.
//
// `canWrite` mirrors the server's read/write split (#924, `saWriteCapabilities`)
// so the page does not offer controls the API would refuse. It is NOT the
// control: every grant and revoke is authorized again server-side, and the
// refusals are rendered rather than pre-empted.
// ─────────────────────────────────────────────────────────────────────

export interface PlatformUsersSectionProps {
  /** Role grants and revokes: `platform.roles.manage`, per the SERVER's answer (#924). */
  canWrite: boolean;
  /**
   * Disabling and re-enabling an account: `platform.accounts.manage` (#546,
   * #924). A different capability from the roles, so support can contain a
   * compromised account without editing the matrix. Defaults to `canWrite`.
   */
  canManageAccounts?: boolean;
  /** Injectable for tests. */
  loadUsers?: typeof fetchPlatformUsers;
  grant?: typeof grantPlatformRole;
  revoke?: typeof revokePlatformRole;
  setStatus?: typeof setAccountStatus;
  /** Closing an account on the person's behalf (#1062). Injectable for tests. */
  closeAccount?: typeof closeAccountCall;
}

/** Where Entra assignments are made. A constant, not configuration: it is Microsoft's. */
export const ENTRA_ADMIN_CENTER_URL = 'https://entra.microsoft.com/';

/**
 * Does Microsoft own this account's lifecycle?
 *
 * AN ENTRA ACCOUNT IS SWITCHED OFF IN ENTRA (#535 review — found by testing).
 * `users.status` does gate an Entra session too, so disabling one here WOULD
 * work — which is exactly why offering it is wrong. It would create a second
 * off-switch that Entra knows nothing about: an account disabled here still
 * signs in everywhere else in the organization, and an account offboarded in
 * Entra still looks «active» on this page. Two sources of truth for «does this
 * person still work here», neither aware of the other.
 *
 * THE API IS UNCHANGED, deliberately. Hiding a control is a courtesy, not a
 * control — the same doctrine the role buttons on this page follow — and a
 * script or a future offboarding job may still need the route. What changes is
 * what the staff directory OFFERS.
 *
 * The same test decides which list an account is in (`splitDirectory`).
 */
export function signsInWithEntra(user: PlatformUser): boolean {
  return user.identities.some((i) => i.provider === 'ENTRA');
}

/**
 * THE SPLIT — the whole of the redesign's «logic», and deliberately nothing
 * more than a partition of rows the server already returned.
 *
 *   • `candidates` — signs in with Microsoft, holds no platform role. Step 2
 *     of the guide: these people have done their part and wait for a role.
 *   • `staff`      — signs in with Microsoft, holds a platform role.
 *   • `local`      — does NOT sign in with Microsoft. On the default listing
 *     the server only returns such an account because it holds a platform
 *     role (`STARPOINT_PEOPLE` in the route), so this is «a platform role
 *     without Microsoft»: the old demo account, the dev staff door.
 *
 * Every row lands in exactly one list.
 */
export function splitDirectory(users: readonly PlatformUser[]): {
  candidates: PlatformUser[];
  staff: PlatformUser[];
  local: PlatformUser[];
} {
  const candidates: PlatformUser[] = [];
  const staff: PlatformUser[] = [];
  const local: PlatformUser[] = [];
  for (const user of users) {
    if (!signsInWithEntra(user)) local.push(user);
    else if (user.platformRoles.length === 0) candidates.push(user);
    else staff.push(user);
  }
  return { candidates, staff, local };
}

type Notice = { kind: 'ok' | 'error'; text: string };

export function PlatformUsersSection({
  canWrite,
  canManageAccounts = canWrite,
  loadUsers = fetchPlatformUsers,
  grant = grantPlatformRole,
  revoke = revokePlatformRole,
  setStatus = setAccountStatus,
  closeAccount = closeAccountCall,
}: PlatformUsersSectionProps) {
  const t = useT();
  const [query, setQuery] = useState('');
  /** What the directory part of the page shows: the overview, or a search. */
  const [result, setResult] = useState<DirectoryResult | null>(null);
  /**
   * The last DEFAULT listing — StarPoint's own people. The step guide reads
   * this rather than `result`, so a search (which reaches customers too)
   * neither empties nor pollutes the list of people waiting for a role.
   */
  const [overview, setOverview] = useState<PlatformUser[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyUser, setBusyUser] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  /**
   * Which account is one click from being switched off.
   *
   * A TWO-STEP INSTEAD OF A DIALOG. `window.confirm` is forbidden here
   * (DOMAIN_RULES §5) and this is the case the rule is about: disabling
   * somebody signs them out of every session immediately, and the row that
   * does it sits in a grid of role buttons that are single-click and harmless.
   * Arming makes the difference visible on the row itself.
   */
  const [armed, setArmed] = useState<string | null>(null);

  const load = useCallback(
    async (q: string) => {
      setLoading(true);
      setNotice(null);
      try {
        const next = await loadUsers(q);
        setResult(next);
        if (next.state === 'ok' && next.scope === 'starpoint') setOverview(next.users);
      } finally {
        setLoading(false);
      }
    },
    [loadUsers],
  );

  useEffect(() => {
    void load('');
  }, [load]);

  const nameOf = (user: PlatformUser) => user.displayName ?? user.email ?? t('saUsers.noName');
  const roleName = useCallback(
    (role: PlatformRole) => {
      const key = roleNameKey(role);
      return key ? t(key) : role;
    },
    [t],
  );

  /** The server's reply, applied to every list the account appears in. */
  const applyRoles = (userId: string, roles: PlatformRole[]) => {
    const patch = (u: PlatformUser) => (u.id === userId ? { ...u, platformRoles: roles } : u);
    setResult((prev) =>
      prev && prev.state === 'ok' ? { ...prev, users: prev.users.map(patch) } : prev,
    );
    setOverview((prev) => (prev ? prev.map(patch) : prev));
  };

  const applyStatus = (userId: string, status: string) => {
    const patch = (u: PlatformUser) => (u.id === userId ? { ...u, status } : u);
    setResult((prev) =>
      prev && prev.state === 'ok' ? { ...prev, users: prev.users.map(patch) } : prev,
    );
    setOverview((prev) => (prev ? prev.map(patch) : prev));
  };

  const changeStatus = async (user: PlatformUser, next: 'active' | 'disabled') => {
    setBusyUser(user.id);
    setNotice(null);
    setArmed(null);
    try {
      const res = await setStatus(user.id, next);
      if (res.state === 'ok') {
        applyStatus(user.id, res.status);
        setNotice({
          kind: 'ok',
          text:
            res.status === 'disabled'
              ? t('saUsers.status.disabled', { count: res.sessionsRevoked })
              : t('saUsers.status.enabled'),
        });
      } else if (res.state === 'last-role-manager' || res.state === 'cannot-disable-self') {
        // The server's own sentence — it names the way out.
        setNotice({ kind: 'error', text: res.message });
      } else if (res.state === 'forbidden') {
        setNotice({ kind: 'error', text: t('saUsers.refused') });
      } else {
        setNotice({ kind: 'error', text: t('saUsers.status.failed') });
      }
    } finally {
      setBusyUser(null);
    }
  };

  // ── CLOSING ON THE PERSON'S BEHALF (#1062) ────────────────────────
  //
  // Armed like the disable — `armed` holds `close:<id>` — because it is the
  // heavier act of the two: it cannot be undone from this page.
  const close = async (user: PlatformUser, reason: ClosureReason, reference: string) => {
    setBusyUser(user.id);
    setNotice(null);
    try {
      const res = await closeAccount(user.id, reason, reference);
      if (res.state === 'ok') {
        setArmed(null);
        applyStatus(user.id, 'closed');
        setNotice({
          kind: 'ok',
          text: t('saUsers.status.closedNow', { count: res.sessionsRevoked }),
        });
      } else if (res.state === 'invalid') {
        setNotice({ kind: 'error', text: t('saUsers.status.closeInvalid') });
      } else if (res.state === 'last-role-manager') {
        setNotice({ kind: 'error', text: res.message || t('saUsers.status.failed') });
      } else if (res.state === 'forbidden') {
        setNotice({ kind: 'error', text: t('saUsers.refused') });
      } else {
        setNotice({ kind: 'error', text: t('saUsers.status.failed') });
      }
    } finally {
      setBusyUser(null);
    }
  };

  /** Grant or revoke ONE role — the same two calls this section always made. */
  const change = async (user: PlatformUser, role: PlatformRole, held: boolean) => {
    setBusyUser(user.id);
    setNotice(null);
    try {
      const res = held ? await revoke(user.id, role) : await grant(user.id, role);
      if (res.state === 'ok') {
        applyRoles(user.id, res.platformRoles);
        // SAID OUT LOUD WHEN A ROW MOVES. Granting the first role takes a
        // person out of step 2 and into the staff list; revoking the last one
        // of a Microsoft account sends them back. A row that silently vanishes
        // from under the pointer reads as a bug.
        if (!held) {
          setNotice({
            kind: 'ok',
            text: t('saUsers.granted', { name: nameOf(user), role: roleName(role) }),
          });
        } else if (res.platformRoles.length === 0 && signsInWithEntra(user)) {
          setNotice({ kind: 'ok', text: t('saUsers.revokedLast', { name: nameOf(user) }) });
        }
        return true;
      }
      if (res.state === 'last-superadmin') {
        // The server's own refusal, shown verbatim — it explains the way out.
        setNotice({ kind: 'error', text: res.message });
      } else if (res.state === 'no-entra-identity') {
        setNotice({ kind: 'error', text: t('saUsers.noEntraIdentity', { name: nameOf(user) }) });
      } else if (res.state === 'forbidden') {
        setNotice({ kind: 'error', text: t('saUsers.refused') });
      } else {
        setNotice({ kind: 'error', text: t('saUsers.roleFailed') });
      }
      return false;
    } finally {
      setBusyUser(null);
    }
  };

  const rowProps = {
    canWrite,
    canManageAccounts,
    busyUser,
    armed,
    roleName,
    onArm: setArmed,
    onToggle: (user: PlatformUser, role: PlatformRole, held: boolean) =>
      void change(user, role, held),
    onStatus: (user: PlatformUser, next: 'active' | 'disabled') => void changeStatus(user, next),
    onClose: (user: PlatformUser, reason: ClosureReason, reference: string) =>
      void close(user, reason, reference),
  };

  const searching = result?.state === 'ok' && result.scope === 'search';

  return (
    <section className="card p-6 mt-6" data-testid="sa-users">
      <h2 className="font-display text-sm font-semibold">{t('saUsers.title')}</h2>
      <p className="text-[11px] text-ink-3 mt-1 max-w-3xl">{t('saUsers.lead')}</p>

      {notice && (
        <p
          role={notice.kind === 'error' ? 'alert' : 'status'}
          className={
            notice.kind === 'error'
              ? 'mt-3 text-[11px] text-rose px-3 py-2 rounded bg-rose/10 border border-rose/20'
              : 'mt-3 text-[11px] text-ink px-3 py-2 rounded bg-lime-bg border border-lime-deep'
          }
          data-testid="sa-users-notice"
        >
          {notice.text}
        </p>
      )}

      <StepGuide
        candidates={overview === null ? null : splitDirectory(overview).candidates}
        canWrite={canWrite}
        busy={busyUser !== null}
        loading={loading}
        roleName={roleName}
        nameOf={nameOf}
        onRefresh={() => {
          setQuery('');
          void load('');
        }}
        onGrant={(user, role) => change(user, role, false)}
      />

      <div className="mt-8">
        <h3 className="font-display text-sm font-semibold">{t('saUsers.directory.title')}</h3>
        <form
          className="mt-3 flex gap-2"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            void load(query.trim());
          }}
        >
          <input
            type="search"
            className="input flex-1 text-xs"
            placeholder={t('saUsers.search.placeholder')}
            aria-label={t('saUsers.search.placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="sa-users-search"
          />
          <button type="submit" className="btn-ghost text-xs" disabled={loading}>
            {loading ? t('saUsers.search.busy') : t('saUsers.search.submit')}
          </button>
          {searching && (
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={loading}
              onClick={() => {
                setQuery('');
                void load('');
              }}
              data-testid="sa-users-search-clear"
            >
              {t('saUsers.search.clear')}
            </button>
          )}
        </form>

        {!canWrite && result?.state === 'ok' && (
          <p className="mt-3 text-[11px] text-ink-3" data-testid="sa-users-readonly">
            {t('saUsers.readOnly')}
          </p>
        )}

        <DirectoryBody result={result} rowProps={rowProps} />
      </div>
    </section>
  );
}

// ── STEP GUIDE ─────────────────────────────────────────────────────────

function StepGuide({
  candidates,
  canWrite,
  busy,
  loading,
  roleName,
  nameOf,
  onRefresh,
  onGrant,
}: {
  /** `null` until the default listing has been read. */
  candidates: PlatformUser[] | null;
  canWrite: boolean;
  busy: boolean;
  loading: boolean;
  roleName: (role: PlatformRole) => string;
  nameOf: (user: PlatformUser) => string;
  onRefresh: () => void;
  onGrant: (user: PlatformUser, role: PlatformRole) => Promise<boolean>;
}) {
  const t = useT();
  const { formatDate } = useLocale();
  const uid = safeId('sa-guide', useId());
  const [person, setPerson] = useState('');
  const [role, setRole] = useState<PlatformRole | ''>('');

  // A pick that is no longer a candidate (granted from the list below, or
  // gone after a refresh) must not linger as a hidden selection.
  const candidateIds = useMemo(() => new Set((candidates ?? []).map((c) => c.id)), [candidates]);
  const chosen = candidateIds.has(person) ? person : '';
  const chosenUser = (candidates ?? []).find((c) => c.id === chosen) ?? null;

  const lastSignIn = (user: PlatformUser) =>
    user.identities.find((i) => i.provider === 'ENTRA')?.lastLoginAt ?? null;

  return (
    <div className="mt-5 rounded-xl border border-line p-5" data-testid="sa-guide">
      <h3 className="font-display text-sm font-semibold">{t('saUsers.guide.title')}</h3>
      <p className="text-[11px] text-ink-3 mt-1 max-w-3xl">{t('saUsers.guide.lead')}</p>

      <ol className="mt-4 space-y-5">
        <li className="flex gap-3" data-testid="sa-guide-step-1">
          <StepNumber n={1} />
          <div className="min-w-0">
            <p className="text-xs font-semibold">{t('saUsers.guide.step1.title')}</p>
            <p className="text-[11px] text-ink-3 mt-1 max-w-3xl">{t('saUsers.guide.step1.body')}</p>
            <a
              href={ENTRA_ADMIN_CENTER_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-block mt-1.5 text-[11px] underline"
              data-testid="sa-guide-entra-link"
            >
              {t('saUsers.guide.step1.link')}
            </a>
          </div>
        </li>

        <li className="flex gap-3" data-testid="sa-guide-step-2">
          <StepNumber n={2} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3">
              <p className="text-xs font-semibold">{t('saUsers.guide.step2.title')}</p>
              <button
                type="button"
                className="text-[11px] underline text-ink-3"
                onClick={onRefresh}
                disabled={loading}
                data-testid="sa-guide-refresh"
              >
                {t('saUsers.guide.step2.refresh')}
              </button>
            </div>
            <p className="text-[11px] text-ink-3 mt-1 max-w-3xl">{t('saUsers.guide.step2.body')}</p>
            {candidates !== null &&
              (candidates.length === 0 ? (
                <p className="mt-2 text-[11px] text-ink-3" data-testid="sa-guide-no-candidates">
                  {t('saUsers.guide.step2.empty')}
                </p>
              ) : (
                <ul className="mt-2 space-y-1" data-testid="sa-guide-candidates">
                  {candidates.map((user) => {
                    const at = lastSignIn(user);
                    return (
                      <li
                        key={user.id}
                        className="text-xs flex flex-wrap items-baseline gap-x-2"
                        data-testid={`sa-guide-candidate-${user.id}`}
                      >
                        <span className="font-medium">{nameOf(user)}</span>
                        {user.email && user.displayName && (
                          <span className="text-[11px] text-ink-3">{user.email}</span>
                        )}
                        {at && (
                          <span className="text-[11px] text-ink-3">
                            {t('saUsers.guide.step2.signedIn', {
                              date: formatDate(new Date(at), {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              }),
                            })}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ))}
          </div>
        </li>

        <li className="flex gap-3" data-testid="sa-guide-step-3">
          <StepNumber n={3} />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold">{t('saUsers.guide.step3.title')}</p>
            <p className="text-[11px] text-ink-3 mt-1 max-w-3xl">{t('saUsers.guide.step3.body')}</p>
            {!canWrite ? (
              <p className="mt-2 text-[11px] text-ink-3" data-testid="sa-guide-readonly">
                {t('saUsers.guide.step3.readOnly')}
              </p>
            ) : candidates && candidates.length > 0 ? (
              <form
                className="mt-2 flex flex-wrap items-end gap-3"
                data-testid="sa-guide-grant-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!chosenUser || !role) return;
                  void onGrant(chosenUser, role).then((ok) => {
                    if (ok) {
                      setPerson('');
                      setRole('');
                    }
                  });
                }}
              >
                <div>
                  <label
                    htmlFor={`${uid}-person`}
                    className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                  >
                    {t('saUsers.guide.step3.person')}
                  </label>
                  <select
                    id={`${uid}-person`}
                    className="input text-xs min-w-[14rem]"
                    value={chosen}
                    onChange={(e) => setPerson(e.target.value)}
                    data-testid="sa-guide-person"
                  >
                    <option value="">{t('saUsers.guide.step3.choosePerson')}</option>
                    {candidates.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.email && user.displayName
                          ? `${user.displayName} — ${user.email}`
                          : nameOf(user)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor={`${uid}-role`}
                    className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                  >
                    {t('saUsers.guide.step3.role')}
                  </label>
                  <select
                    id={`${uid}-role`}
                    className="input text-xs min-w-[12rem]"
                    value={role}
                    onChange={(e) => setRole(e.target.value as PlatformRole | '')}
                    data-testid="sa-guide-role"
                  >
                    <option value="">{t('saUsers.guide.step3.chooseRole')}</option>
                    {PLATFORM_ROLES.map((key) => (
                      <option key={key} value={key}>
                        {roleName(key)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  className="btn-lime text-xs"
                  disabled={busy || !chosenUser || !role}
                  data-testid="sa-guide-grant"
                >
                  {t('saUsers.guide.step3.submit')}
                </button>
              </form>
            ) : (
              <p className="mt-2 text-[11px] text-ink-3" data-testid="sa-guide-nobody">
                {t('saUsers.guide.step3.nobody')}
              </p>
            )}
          </div>
        </li>
      </ol>
    </div>
  );
}

function StepNumber({ n }: { n: number }) {
  return (
    <span
      aria-hidden="true"
      className="shrink-0 w-6 h-6 rounded-full bg-lime text-ink text-[11px] font-semibold flex items-center justify-center"
    >
      {n}
    </span>
  );
}

// ── THE DIRECTORY ──────────────────────────────────────────────────────

interface RowProps {
  canWrite: boolean;
  canManageAccounts: boolean;
  busyUser: string | null;
  armed: string | null;
  roleName: (role: PlatformRole) => string;
  onArm: (userId: string | null) => void;
  onToggle: (user: PlatformUser, role: PlatformRole, held: boolean) => void;
  onStatus: (user: PlatformUser, next: 'active' | 'disabled') => void;
  onClose: (user: PlatformUser, reason: ClosureReason, reference: string) => void;
}

function DirectoryBody({
  result,
  rowProps,
}: {
  result: DirectoryResult | null;
  rowProps: RowProps;
}) {
  const t = useT();
  if (!result) return null;
  if (result.state === 'no-api') {
    return (
      <p className="mt-3 text-[11px] text-ink-3" data-testid="sa-users-state">
        {t('saUsers.state.noApi')}
      </p>
    );
  }
  if (result.state === 'forbidden') {
    return (
      <p className="mt-3 text-[11px] text-rose" data-testid="sa-users-state">
        {t('saUsers.state.forbidden')}
      </p>
    );
  }
  if (result.state === 'error') {
    return (
      <p className="mt-3 text-[11px] text-rose" data-testid="sa-users-state">
        {result.status
          ? t('saUsers.state.errorStatus', { status: String(result.status) })
          : t('saUsers.state.error')}
      </p>
    );
  }
  if (result.users.length === 0) {
    return (
      <p className="mt-3 text-[11px] text-ink-3" data-testid="sa-users-state">
        {t('saUsers.state.empty')}
      </p>
    );
  }

  // A SEARCH IS THE OLD FLAT LIST, unchanged: it deliberately reaches customer
  // accounts («why can't this person see X?» is usually asked about one), and
  // every row says which kind it is.
  if (result.scope === 'search') {
    return (
      <>
        <ul className="mt-4 space-y-3" data-testid="sa-users-list">
          {result.users.map((user) => (
            <UserRow key={user.id} user={user} {...rowProps} />
          ))}
        </ul>
        <p className="mt-3 text-[11px] text-ink-3" data-testid="sa-users-scope">
          {t('saUsers.search.scope')}
        </p>
        {result.truncated && (
          <p className="mt-3 text-[11px] text-ink-3">{t('saUsers.truncated')}</p>
        )}
      </>
    );
  }

  const { staff, local } = splitDirectory(result.users);
  return (
    <>
      <div className="mt-5">
        <h4 className="text-[10px] font-mono uppercase tracking-wider text-ink-3">
          {t('saUsers.staff.title')}
        </h4>
        <p className="text-[11px] text-ink-3 mt-1 max-w-3xl">{t('saUsers.staff.lead')}</p>
        {staff.length === 0 ? (
          <p className="mt-3 text-[11px] text-ink-3" data-testid="sa-users-staff-empty">
            {t('saUsers.staff.empty')}
          </p>
        ) : (
          <ul className="mt-3 space-y-3" data-testid="sa-users-list">
            {staff.map((user) => (
              <UserRow key={user.id} user={user} {...rowProps} />
            ))}
          </ul>
        )}
      </div>

      <div
        className="mt-8 rounded-xl border border-dashed border-line bg-orange-soft p-5"
        data-testid="sa-users-local"
      >
        <h4 className="font-display text-sm font-semibold">{t('saUsers.local.title')}</h4>
        <p className="text-[11px] text-ink-3 mt-1 max-w-3xl">{t('saUsers.local.lead')}</p>
        {local.length === 0 ? (
          <p className="mt-3 text-[11px] text-ink-3" data-testid="sa-users-local-empty">
            {t('saUsers.local.empty')}
          </p>
        ) : (
          <ul className="mt-3 space-y-3" data-testid="sa-users-local-list">
            {local.map((user) => (
              <LocalRow key={user.id} user={user} {...rowProps} />
            ))}
          </ul>
        )}
      </div>
      {result.truncated && <p className="mt-3 text-[11px] text-ink-3">{t('saUsers.truncated')}</p>}
    </>
  );
}

/** Name, address, status and how the account signs in — the same on every row. */
function RowHeading({ user }: { user: PlatformUser }) {
  const t = useT();
  const providers = user.identities.map((i) => providerLabel(i.provider, t));
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-xs font-medium">{user.displayName ?? t('saUsers.noName')}</span>
        {user.email && <span className="text-[11px] text-ink-3">{user.email}</span>}
        {user.status !== 'active' && <span className="text-[11px] text-rose">({user.status})</span>}
      </div>
      <p className="text-[11px] text-ink-3 mt-0.5">
        {/* SAID ON THE ROW, not inferred from which list you are looking at.
            A search returns both kinds at once, and «is this a colleague or a
            customer» is the one thing you must not get wrong before handing
            out a platform role. */}
        {!user.starpoint && (
          <span className="text-orange" data-testid={`sa-user-customer-${user.id}`}>
            {t('saUsers.customer')} ·{' '}
          </span>
        )}
        {providers.length > 0 ? providers.join(', ') : t('saUsers.noSignIn')}
        {user.tenantMembershipCount > 0 &&
          ` · ${t('saUsers.clubs', { count: user.tenantMembershipCount })}`}
      </p>
    </>
  );
}

/** A provider is a CODE from the server; the browser decides the words. */
function providerLabel(provider: string, t: ReturnType<typeof useT>): string {
  if (provider === 'ENTRA') return t('saUsers.provider.ENTRA');
  if (provider === 'LOCAL') return t('saUsers.provider.LOCAL');
  return provider;
}

/** A staff (or search) row: every platform role as a toggle, as before. */
function UserRow({
  user,
  canWrite,
  canManageAccounts,
  busyUser,
  armed,
  roleName,
  onArm,
  onToggle,
  onStatus,
  onClose,
}: RowProps & { user: PlatformUser }) {
  return (
    <li className="border-t border-white/10 pt-3">
      <RowHeading user={user} />
      <div className="mt-2 flex flex-wrap gap-1.5">
        {PLATFORM_ROLES.map((role) => {
          const held = user.platformRoles.includes(role);
          return (
            <button
              key={role}
              type="button"
              disabled={!canWrite || busyUser === user.id}
              onClick={() => onToggle(user, role, held)}
              className={held ? 'btn-lime text-[11px]' : 'btn-ghost text-[11px]'}
              data-testid={`sa-role-${user.id}-${role}`}
              aria-pressed={held}
            >
              {roleName(role)}
            </button>
          );
        })}
      </div>
      {canManageAccounts && !signsInWithEntra(user) && (
        <AccountActions
          user={user}
          busy={busyUser === user.id}
          armed={armed === user.id}
          closeArmed={armed === `close:${user.id}`}
          onArm={onArm}
          onStatus={onStatus}
          onClose={onClose}
        />
      )}
    </li>
  );
}

/**
 * An account with a platform role and no Microsoft sign-in.
 *
 * ONLY THE WAY OUT IS OFFERED: the roles it holds, each with the existing
 * revoke, and the existing account actions. No grant buttons — the onboarding
 * path is Microsoft first, and this block exists so leftovers are cleaned up,
 * not added to. (A search still reaches every account with the full row, as it
 * always did; nothing the API allows has been taken away.)
 */
function LocalRow({
  user,
  canWrite,
  canManageAccounts,
  busyUser,
  armed,
  roleName,
  onArm,
  onToggle,
  onStatus,
  onClose,
}: RowProps & { user: PlatformUser }) {
  const t = useT();
  return (
    <li className="border-t border-line pt-3 first:border-t-0 first:pt-0">
      <RowHeading user={user} />
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {user.platformRoles.length === 0 && (
          <span className="text-[11px] text-ink-3">{t('saUsers.local.noRole')}</span>
        )}
        {user.platformRoles.map((role) => (
          <span
            key={role}
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-3 py-1 text-[11px]"
            data-testid={`sa-local-role-${user.id}-${role}`}
          >
            {roleName(role)}
            {canWrite && (
              <button
                type="button"
                className="underline text-rose"
                disabled={busyUser === user.id}
                onClick={() => onToggle(user, role, true)}
                aria-label={t('saUsers.local.revokeLabel', {
                  role: roleName(role),
                  name: user.displayName ?? user.email ?? t('saUsers.noName'),
                })}
                data-testid={`sa-revoke-${user.id}-${role}`}
              >
                {t('saUsers.local.revoke')}
              </button>
            )}
          </span>
        ))}
      </div>
      {canManageAccounts && (
        <AccountActions
          user={user}
          busy={busyUser === user.id}
          armed={armed === user.id}
          closeArmed={armed === `close:${user.id}`}
          onArm={onArm}
          onStatus={onStatus}
          onClose={onClose}
        />
      )}
    </li>
  );
}

/**
 * The lifecycle action on one account: switching it off, or back on.
 *
 * Kept apart from the role buttons above deliberately: those are the same
 * shape of decision repeated five times, this is a different and heavier one.
 * It is shown only to somebody the server would let through — the refusal is
 * still the server's, but offering a control that always fails is its own
 * kind of lie.
 */
function AccountActions({
  user,
  busy,
  armed,
  closeArmed,
  onArm,
  onStatus,
  onClose,
}: {
  user: PlatformUser;
  busy: boolean;
  armed: boolean;
  closeArmed: boolean;
  onArm: (userId: string | null) => void;
  onStatus: (user: PlatformUser, next: 'active' | 'disabled') => void;
  onClose: (user: PlatformUser, reason: ClosureReason, reference: string) => void;
}) {
  const t = useT();
  const disabled = user.status !== 'active';

  // A CLOSED ACCOUNT HAS NO SWITCH (#1062). The server refuses to re-enable it,
  // and offering «Aktiver kontoen» would be a control that always fails.
  if (user.status === 'closed') {
    return (
      <p className="mt-2 text-[11px] text-ink-3" data-testid={`sa-closed-${user.id}`}>
        {t('saUsers.account.closed')}
      </p>
    );
  }
  if (closeArmed) {
    return <CloseAccountForm user={user} busy={busy} onArm={onArm} onClose={onClose} />;
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {disabled ? (
        <button
          type="button"
          className="btn-ghost text-[11px]"
          disabled={busy}
          onClick={() => onStatus(user, 'active')}
          data-testid={`sa-enable-${user.id}`}
        >
          {t('saUsers.account.enable')}
        </button>
      ) : armed ? (
        <>
          <button
            type="button"
            className="btn-danger text-[11px]"
            disabled={busy}
            onClick={() => onStatus(user, 'disabled')}
            data-testid={`sa-disable-confirm-${user.id}`}
          >
            {t('saUsers.account.disableConfirm')}
          </button>
          <button
            type="button"
            className="btn-ghost text-[11px]"
            disabled={busy}
            onClick={() => onArm(null)}
            data-testid={`sa-disable-cancel-${user.id}`}
          >
            {t('saUsers.account.cancel')}
          </button>
          <span className="text-[11px] text-ink-3">{t('saUsers.account.disableHint')}</span>
        </>
      ) : (
        <button
          type="button"
          className="btn-ghost text-[11px]"
          disabled={busy}
          onClick={() => onArm(user.id)}
          data-testid={`sa-disable-${user.id}`}
        >
          {t('saUsers.account.disable')}
        </button>
      )}
      {!armed && (
        <button
          type="button"
          className="btn-ghost text-[11px]"
          disabled={busy}
          onClick={() => onArm(`close:${user.id}`)}
          data-testid={`sa-close-${user.id}`}
        >
          {t('saUsers.account.close')}
        </button>
      )}
    </div>
  );
}

/**
 * Closing an account on the person's behalf: a reason CODE and a ticket
 * reference, both required, then an explicit confirmation (#1062). The
 * reference is checked here against the server's own rule so the button says
 * what is wrong before anything is sent; the server checks it again.
 */
function CloseAccountForm({
  user,
  busy,
  onArm,
  onClose,
}: {
  user: PlatformUser;
  busy: boolean;
  onArm: (userId: string | null) => void;
  onClose: (user: PlatformUser, reason: ClosureReason, reference: string) => void;
}) {
  const t = useT();
  const uid = safeId('sa-close', useId());
  const [reason, setReason] = useState<ClosureReason | ''>('');
  const [reference, setReference] = useState('');
  const valid = reason !== '' && SUPPORT_REFERENCE.test(reference.trim());
  return (
    <div className="mt-2 flex flex-col gap-2" data-testid={`sa-close-form-${user.id}`}>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label htmlFor={`${uid}-reason`} className="text-[11px] text-ink-2">
            {t('saUsers.account.closeReason')}
          </label>
          <select
            id={`${uid}-reason`}
            className="input text-[11px]"
            value={reason}
            onChange={(e) => setReason(e.target.value as ClosureReason | '')}
            data-testid={`sa-close-reason-${user.id}`}
          >
            <option value="">—</option>
            {CLOSURE_REASONS.map((r) => (
              <option key={r} value={r}>
                {t(`saUsers.account.closeReason.${r}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={`${uid}-reference`} className="text-[11px] text-ink-2">
            {t('saUsers.account.closeReference')}
          </label>
          <input
            id={`${uid}-reference`}
            className="input text-[11px]"
            value={reference}
            maxLength={64}
            aria-describedby={`${uid}-reference-hint`}
            onChange={(e) => setReference(e.target.value)}
            data-testid={`sa-close-reference-${user.id}`}
          />
        </div>
      </div>
      <p id={`${uid}-reference-hint`} className="text-[11px] text-ink-3">
        {t('saUsers.account.closeReferenceHint')}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-danger text-[11px]"
          disabled={busy || !valid}
          onClick={() => reason !== '' && onClose(user, reason, reference.trim())}
          data-testid={`sa-close-confirm-${user.id}`}
        >
          {t('saUsers.account.closeConfirm')}
        </button>
        <button
          type="button"
          className="btn-ghost text-[11px]"
          disabled={busy}
          onClick={() => onArm(null)}
          data-testid={`sa-close-cancel-${user.id}`}
        >
          {t('saUsers.account.cancel')}
        </button>
        <span className="text-[11px] text-ink-3">{t('saUsers.account.closeHint')}</span>
      </div>
    </div>
  );
}
